// PageCampaign component
// This component is used to display the page for creating and editing campaigns

import React, { useEffect, useState } from 'react';
import {
  alpha,
  Box,
  Button,
  FormControl,
  TextField,
  useTheme,
  Typography,
  Select,
  MenuItem,
  InputLabel, RadioGroup, FormControlLabel, Radio,
} from '@mui/material';
import { APICreateCampaign, APIUpdateCampaign } from '../../utils/apiCalled';
import BackButton from '../backButton';

const COUNTRY_CODES = [
  { code: 'MY', name: 'Malaysia' },
  { code: 'OM', name: 'Oman' },
];

export default function PageCampaign(
  {
    campaign,
    setOpen,
    setSuccMsg,
    setErrMsg,
    setVariant,
    setMainMsg,
    setModalTriggerAPICalled,
    handleCloseMoreCampaigns,
    fetchCampaigns,
  },
) {
  const [campaignName, setCampaignName] = useState('');
  const [campaignDescription, setCampaignDescription] = useState('');
  const [alertTime, setAlertTime] = useState({
    onDemand: false,
    sendNow: false,
    scheduled: false,
  });
  const [targetAudience, setTargetAudience] = useState('Everyone');
  const [selectedCountryCode, setSelectedCountryCode] = useState('');
  const [campaignMessage, setCampaignMessage] = useState('');

  // const [loading, setLoading] = useState(false);
  const [posLoading, setPosLoading] = useState(false);
  const [campaignData, setCampaignData] = useState({});
  const [disabledButton, setDisabledButton] = useState(true);

  const theme = useTheme();

  useEffect(() => {
    if (campaign) {
      setCampaignName(campaign.campaignName || '');
      setCampaignDescription(campaign.campaignDescription || '');

      // Check if targetAudience contains country codes (MY, OM, etc.)
      const countryCodeValues = COUNTRY_CODES.map((c) => c.code);
      const isCountryCode = campaign.targetAudience
        && campaign.targetAudience !== 'Everyone'
        && (countryCodeValues.includes(campaign.targetAudience)
            || campaign.targetAudience.split(',').some((code) => countryCodeValues.includes(code.trim())));

      if (isCountryCode) {
        setTargetAudience('Country Code');
        // Extract single country code from targetAudience
        if (campaign.selectedCountryCodes && campaign.selectedCountryCodes.length > 0) {
          setSelectedCountryCode(campaign.selectedCountryCodes[0]);
        } else if (typeof campaign.targetAudience === 'string') {
          // Get first valid country code from targetAudience
          const codes = campaign.targetAudience.split(',')
            .map((code) => code.trim())
            .filter((code) => countryCodeValues.includes(code));
          setSelectedCountryCode(codes[0] || '');
        } else if (countryCodeValues.includes(campaign.targetAudience)) {
          setSelectedCountryCode(campaign.targetAudience);
        }
      } else {
        setTargetAudience(campaign.targetAudience || 'Everyone');
        setSelectedCountryCode('');
      }

      setCampaignMessage(
        typeof campaign.campaignMessage === 'object'
          ? campaign.campaignMessage.body || ''
          : campaign.campaignMessage || '',
      );
      // Set alertTime from campaign
      if (campaign.alertTime) {
        setAlertTime({
          onDemand: campaign.alertTime.onDemand || false,
          sendNow: campaign.alertTime.sendNow || false,
          scheduled: campaign.alertTime.scheduled || false,
        });
      }
    }
  }, [campaign]);

  useEffect(() => {
  // Create the campaign data object
    const data = {
      campaignName,
      campaignDescription,
      alertTime,
      targetAudience: targetAudience === 'Country Code' && selectedCountryCode
        ? selectedCountryCode
        : targetAudience,
      ...(targetAudience === 'Country Code' && selectedCountryCode && {
        selectedCountryCodes: [selectedCountryCode],
      }),
      campaignMessage: {
        title: campaignName,
        body: campaignMessage,
      },
    };
    setCampaignData(data);
  }, [
    campaignName,
    campaignDescription,
    alertTime,
    targetAudience,
    selectedCountryCode,
    campaignMessage,
  ]);

  const fontHeaderStyle = {
    color: theme.palette.grey[700],
    fontWeight: 'fontWeightMedium',
    mr: 5,
    minWidth: '250px',
  };

  const formControlStyle = {
    mb: 3,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  };

  const buttonStyle = {
    border: '1px solid',
    borderColor: 'grey.300',
    px: 2,
    pt: 1,
    pb: 1,
    mr: 2,
    color: theme.palette.grey[700],
    backgroundColor: 'background.paper',
    '&:hover': { bgcolor: alpha(theme.palette.grey[500], 0.08) },
  };

  const confirmButtonStyle = {
    border: 'none',
    px: 2,
    pt: 1,
    pb: 1,
    mr: 2,
    bgcolor: theme.palette.primary.main,
    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.90) },
    color: 'white',
    fontWeight: 'fontWeightMedium',
    '&.Mui-disabled': { bgcolor: theme.palette.primary.main, color: 'white', opacity: '0.6' },
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    switch (name) {
      case 'campaignName':
        setCampaignName(value);
        break;
      case 'campaignDescription':
        setCampaignDescription(value);
        break;
      case 'campaignMessage':
        setCampaignMessage(value);
        break;
      case 'targetAudience':
        setTargetAudience(value);
        if (value === 'Everyone') {
          setSelectedCountryCode('');
        }
        break;
      default:
        break;
    }
  };

  const handleCountryCodeChange = (event) => {
    setSelectedCountryCode(event.target.value);
  };

  const handleAlertTimeChange = (e) => {
    const { value } = e.target;
    setAlertTime({
      onDemand: value === 'onDemand',
      sendNow: value === 'sendNow',
      scheduled: value === 'scheduled',
    });
  };

  const removeAllState = () => {
    setCampaignName('');
    setCampaignDescription('');
    setCampaignMessage('');
    setTargetAudience('Everyone');
    setSelectedCountryCode('');
    setAlertTime({
      onDemand: false,
      sendNow: false,
      scheduled: false,
    });
  };

  const apiAddEditCalled = async (value, id) => {
    try {
    // Check if campaign exists before trying to update
      if (campaign && campaign._id) {
        const result = await APIUpdateCampaign(setPosLoading, id, value);
        if (result.status === 200) {
          setPosLoading(false);
          removeAllState();
          setOpen(true);
          setVariant('success');
          setSuccMsg(`Campaign "${value.campaignName}" has successfully been updated`);
          setMainMsg('Campaign Successfully Updated');
          setModalTriggerAPICalled(true);
          fetchCampaigns();
          handleCloseMoreCampaigns();
        } else {
          setPosLoading(false);
          removeAllState();
          const errorResponse = result.response?.data;
          const errorMessage = errorResponse?.message || 'Failed to update campaign';
          setOpen(true);
          setVariant('error');
          setErrMsg(errorMessage);
          setMainMsg('Campaign Submission Unsuccessful');
          setModalTriggerAPICalled(true);
          fetchCampaigns();
          handleCloseMoreCampaigns();
        }
      } else {
        // Create campaign if it doesn't exist
        const result = await APICreateCampaign(setPosLoading, value);
        if (result.status === 200) {
          setPosLoading(false);
          removeAllState();
          setOpen(true);
          setVariant('success');
          setSuccMsg(`Campaign "${value.campaignName}" has successfully been created`);
          setMainMsg('Campaign Successfully Created');
          setModalTriggerAPICalled(true);
          fetchCampaigns();
          handleCloseMoreCampaigns();
        } else {
          setPosLoading(false);
          removeAllState();
          const errorResponse = result.response?.data;
          const errorMessage = errorResponse?.message || 'Failed to create campaign';
          setOpen(true);
          setVariant('error');
          setErrMsg(errorMessage);
          setMainMsg('Campaign Submission Unsuccessful');
          setModalTriggerAPICalled(true);
          fetchCampaigns();
          handleCloseMoreCampaigns();
        }
      }
    } catch (err) {
      setPosLoading(false);
      removeAllState();
      setOpen(true);
      setVariant('error');
      setErrMsg(`Error! ${err.message || 'Please try again'}`);
      setMainMsg('Error Encountered');
      setModalTriggerAPICalled(true);
      fetchCampaigns();
      handleCloseMoreCampaigns();
    }
  };

  const handleSubmit = () => {
    // Only submit if campaignData is complete and valid
    if (disabledButton) {
      setOpen(true);
      setVariant('error');
      setErrMsg('Please fill all required fields');
      return;
    }
    apiAddEditCalled(campaignData, campaign ? campaign._id : null);
  };

  useEffect(() => {
    const isFormFilled = campaignName
      && campaignDescription
      && campaignMessage
      && (alertTime.onDemand || alertTime.sendNow || alertTime.scheduled);

    // Compare targetAudience - if Country Code, compare the single code
    const currentTargetAudience = targetAudience === 'Country Code' && selectedCountryCode
      ? selectedCountryCode
      : targetAudience;
    const campaignTargetAudience = campaign?.targetAudience || 'Everyone';

    const isFormUnchanged = campaign
      && campaignName === campaign.campaignName
      && campaignDescription === campaign.campaignDescription
      && campaignMessage === (typeof campaign.campaignMessage === 'object' ? campaign.campaignMessage.body : campaign.campaignMessage)
      && currentTargetAudience === campaignTargetAudience
      && selectedCountryCode === (campaign.selectedCountryCodes?.[0] || campaign.targetAudience || '')
      && alertTime.onDemand === (campaign.alertTime?.onDemand || false)
      && alertTime.sendNow === (campaign.alertTime?.sendNow || false)
      && alertTime.scheduled === (campaign.alertTime?.scheduled || false);

    // Validate country codes if target audience is not "Everyone"
    const isCountryCodesValid = targetAudience === 'Everyone' || selectedCountryCode;

    setDisabledButton(!isFormFilled || !isCountryCodesValid || isFormUnchanged);
  }, [
    campaignName,
    campaignDescription,
    campaignMessage,
    alertTime,
    targetAudience,
    selectedCountryCode,
    campaign,
  ]);

  const getSelectedAlertTime = () => {
    if (alertTime.onDemand) return 'onDemand';
    if (alertTime.sendNow) return 'sendNow';
    if (alertTime.scheduled) return 'scheduled';
    return '';
  };

  return (
    <Box sx={{ mt: 2 }}>
      <BackButton back={handleCloseMoreCampaigns} type="campaign" />
      <Box sx={{
        mt: 2, pb: 4, display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
      }}
      >
        <Box>
          <Typography variant="h4">{campaign ? 'Edit Campaign' : 'Create Campaign'}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'right' }}>
          <Button sx={buttonStyle} onClick={() => handleCloseMoreCampaigns()}>
            <Typography sx={{ fontWeight: 'fontWeightMedium' }}>
              Cancel
            </Typography>
          </Button>
          <Button
            sx={confirmButtonStyle}
            disabled={disabledButton || posLoading}
            onClick={() => handleSubmit(campaignData)}
          >
            <Typography sx={{ fontWeight: 'fontWeightMedium' }}>
              {campaign ? 'Update' : 'Create'}
            </Typography>
          </Button>
        </Box>
      </Box>

      <Box sx={{ padding: 1, borderTop: '1px solid', borderColor: theme.palette.grey[300] }}>
        <Box sx={{ width: '70%', mt: 2 }}>
          <FormControl sx={formControlStyle}>
            <Box sx={{
              display: 'flex', flexDirection: 'column', maxWidth: '250px', mr: 5,
            }}
            >
              <Typography sx={fontHeaderStyle}>
                Campaign Name
                {' '}
                <span style={{ color: 'red' }}>*</span>
              </Typography>
            </Box>
            <TextField
              label="Enter Campaign Name"
              name="campaignName"
              value={campaignName}
              onChange={handleInputChange}
              sx={{ flexGrow: 1 }}
            />
          </FormControl>

          <FormControl sx={formControlStyle}>
            <Box sx={{
              display: 'flex', flexDirection: 'column', maxWidth: '250px', mr: 5,
            }}
            >
              <Typography sx={fontHeaderStyle}>
                Campaign Description
                {' '}
                <span style={{ color: 'red' }}>*</span>
              </Typography>
              <Typography variant="caption" sx={{ color: theme.palette.grey[500], mt: 0.5 }}>
                Add a brief description to explain the purpose of the campaign.
              </Typography>
            </Box>
            <TextField
              label="Example: Campaign to inform user on upcoming promotion"
              name="campaignDescription"
              value={campaignDescription}
              onChange={handleInputChange}
              sx={{ flexGrow: 1 }}
              multiline
              rows={3}
              required
              InputLabelProps={{ required: false }}
            />
          </FormControl>

          <FormControl sx={formControlStyle}>
            <Box sx={{
              display: 'flex', flexDirection: 'column', maxWidth: '250px', mr: 5,
            }}
            >
              <Typography sx={fontHeaderStyle}>
                Alert Time
                {' '}
                <span style={{ color: 'red' }}>*</span>
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: theme.palette.grey[500], mt: 0.5 }}
              >
                This determines the time at
                which the notification campaign will be sent out to users.
              </Typography>
              <Typography variant="caption" sx={{ color: theme.palette.grey[500], mt: 0.5 }}>
                Alerts will be triggered based on the Asia/Kuala Lumpur UTC+08:00 time zone.
              </Typography>
            </Box>
            <FormControl sx={{ flexGrow: 1 }}>
              <RadioGroup
                value={getSelectedAlertTime()}
                onChange={handleAlertTimeChange}
                name="alertTime"
                sx={{
                  '& .MuiFormControlLabel-label': {
                    fontWeight: 'fontWeightLight',
                    fontSize: '14px',
                  },
                }}
              >
                <FormControlLabel value="sendNow" control={<Radio />} label="Send Now" disabled={Boolean(campaign && campaign._id)} />
                <FormControlLabel value="onDemand" control={<Radio />} label="On Demand" disabled={Boolean(campaign && campaign._id)} />
                <FormControlLabel value="scheduled" control={<Radio />} label="Scheduled" disabled />
              </RadioGroup>
            </FormControl>
          </FormControl>

          <FormControl sx={formControlStyle}>
            <Box sx={{
              display: 'flex', flexDirection: 'column', maxWidth: '250px', mr: 5,
            }}
            >
              <Typography sx={fontHeaderStyle}>
                Target Audience
                {' '}
                <span style={{ color: 'red' }}>*</span>
              </Typography>
              <Typography variant="caption" sx={{ color: theme.palette.grey[500], mt: 0.5 }}>
                This determines the audience who would receive this campaign message.
              </Typography>
            </Box>
            <FormControl fullWidth sx={{ flexGrow: 1 }}>
              <InputLabel>Target Audience</InputLabel>
              <Select
                label="Target Audience"
                name="targetAudience"
                value={targetAudience}
                onChange={handleInputChange}
                sx={{ flexGrow: 1 }}
              >
                <MenuItem value="Everyone">Everyone</MenuItem>
                <MenuItem value="Country Code">Country Code</MenuItem>
              </Select>
            </FormControl>
          </FormControl>

          {targetAudience === 'Country Code' && (
            <FormControl sx={formControlStyle}>
              <Box sx={{
                display: 'flex', flexDirection: 'column', maxWidth: '250px', mr: 5,
              }}
              >
                <Typography sx={fontHeaderStyle}>
                  Select Country Code
                  {' '}
                  <span style={{ color: 'red' }}>*</span>
                </Typography>
                <Typography variant="caption" sx={{ color: theme.palette.grey[500], mt: 0.5 }}>
                  Select a country code to target a specific country.
                </Typography>
              </Box>
              <FormControl fullWidth sx={{ flexGrow: 1 }}>
                <InputLabel>Country Code</InputLabel>
                <Select
                  value={selectedCountryCode}
                  onChange={handleCountryCodeChange}
                  label="Country Code"
                >
                  {COUNTRY_CODES.map((country) => (
                    <MenuItem key={country.code} value={country.code}>
                      {country.code}
                      {' '}
                      -
                      {' '}
                      {country.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </FormControl>
          )}

          <FormControl sx={formControlStyle}>
            <Box sx={{
              display: 'flex', flexDirection: 'column', maxWidth: '250px', mr: 5,
            }}
            >
              <Typography sx={fontHeaderStyle}>
                Campaign Message
                {' '}
                <span style={{ color: 'red' }}>*</span>
              </Typography>
              <Typography variant="caption" sx={{ color: theme.palette.grey[500], mt: 0.5 }}>
                Enter the message to be sent out here.
              </Typography>
            </Box>
            <TextField
              label="Example: Dear users we will be closing the app for maintenance"
              name="campaignMessage"
              value={campaignMessage}
              onChange={handleInputChange}
              sx={{ flexGrow: 1 }}
              multiline
              rows={4}
            />
          </FormControl>
        </Box>
      </Box>
    </Box>
  );
}
